// 학업시간표 관리 페이지
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  Heading,
  Divider,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Switch,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Checkbox,
  CheckboxGroup,
  Wrap,
  WrapItem,
  VStack
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon, EditIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { useAuth } from 'contexts/AuthContext';
import {
  getAcademicSchedules,
  getActiveAcademicSchedule,
  createAcademicSchedule,
  updateAcademicSchedule,
  deleteAcademicSchedule,
  getAllSemesters,
  canRegisterSchedule,
  getUserSemesterWorkers
} from 'utils/firestoreUtils';
import { getDayNameKorean } from 'utils/scheduleUtils';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_NAMES_KR = {
  monday: '월요일',
  tuesday: '화요일',
  wednesday: '수요일',
  thursday: '목요일',
  friday: '금요일',
  saturday: '토요일',
  sunday: '일요일'
};

function AcademicSchedule() {
  const { currentUser } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [schedules, setSchedules] = useState([]);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);

  // 폼 데이터
  const [formData, setFormData] = useState({
    semesterId: '',
    semesterName: '',
    isActive: true,
    weeklySchedule: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    }
  });

  // 시간 추가 폼
  const [newClass, setNewClass] = useState({
    start: '',
    end: '',
    subject: '',
    selectedDays: [] // 여러 요일 선택
  });
  const [selectedDay, setSelectedDay] = useState('monday');

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [allSchedules, active, allSemesters, userWorkers] = await Promise.all([
        getAcademicSchedules(currentUser.uid),
        getActiveAcademicSchedule(currentUser.uid),
        getAllSemesters(),
        getUserSemesterWorkers(currentUser.uid)
      ]);
      
      // 근무자로 선정된 학기만 필터링
      const workerSemesterIds = userWorkers.map(w => w.semesterId);
      const filteredSemesters = allSemesters.filter(sem => workerSemesterIds.includes(sem.id));
      
      setSchedules(allSchedules);
      setActiveSchedule(active);
      setSemesters(filteredSemesters);
    } catch (error) {
      console.error('데이터 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '데이터를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewSchedule = () => {
    // 입력 가능한 학기 확인 + 이미 시간표가 있는 학기 제외
    const existingSemesterIds = schedules.map(s => s.semesterId);
    const availableSemesters = semesters.filter(sem => 
      canRegisterSchedule(sem) && !existingSemesterIds.includes(sem.id)
    );
    
    if (availableSemesters.length === 0) {
      const hasAllSchedules = semesters.every(sem => existingSemesterIds.includes(sem.id));
      const message = hasAllSchedules
        ? '모든 학기에 이미 시간표가 등록되어 있습니다.'
        : '현재 시간표를 등록할 수 있는 학기가 없습니다. 관리자에게 문의하세요.';
      
      toast({
        title: '등록 불가',
        description: message,
        status: 'warning',
        duration: 5000
      });
      return;
    }

    setEditingSchedule(null);
    setSelectedSemester(null);
    setFormData({
      semesterId: '',
      semesterName: '',
      isActive: true,
      weeklySchedule: {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
      }
    });
    setNewClass({ start: '', end: '', subject: '', selectedDays: [] });
    onOpen();
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setSelectedSemester(semesters.find(s => s.id === schedule.semesterId));
    setFormData({
      semesterId: schedule.semesterId,
      semesterName: schedule.semesterName,
      isActive: schedule.isActive,
      weeklySchedule: schedule.weeklySchedule
    });
    onOpen();
  };

  const handleSemesterSelect = (semesterId) => {
    const semester = semesters.find(s => s.id === semesterId);
    if (semester) {
      setSelectedSemester(semester);
      setFormData({
        ...formData,
        semesterId: semester.id,
        semesterName: semester.semesterName
      });
    }
  };

  const handleAddClass = () => {
    if (!newClass.start || !newClass.end || !newClass.subject) {
      toast({
        title: '입력 오류',
        description: '모든 항목을 입력해주세요.',
        status: 'warning',
        duration: 2000
      });
      return;
    }

    if (!newClass.selectedDays || newClass.selectedDays.length === 0) {
      toast({
        title: '입력 오류',
        description: '최소 1개 이상의 요일을 선택해주세요.',
        status: 'warning',
        duration: 2000
      });
      return;
    }

    // 선택된 요일들에 모두 추가
    const updatedSchedule = { ...formData.weeklySchedule };
    newClass.selectedDays.forEach(day => {
      updatedSchedule[day] = [
        ...updatedSchedule[day],
        { 
          start: newClass.start, 
          end: newClass.end, 
          subject: newClass.subject 
        }
      ];
    });

    setFormData(prev => ({
      ...prev,
      weeklySchedule: updatedSchedule
    }));

    setNewClass({ start: '', end: '', subject: '', selectedDays: [] });
    
    toast({
      title: '추가 완료',
      description: `${newClass.selectedDays.length}개 요일에 일정이 추가되었습니다.`,
      status: 'success',
      duration: 2000
    });
  };

  const handleDeleteClass = (day, index) => {
    setFormData(prev => ({
      ...prev,
      weeklySchedule: {
        ...prev.weeklySchedule,
        [day]: prev.weeklySchedule[day].filter((_, i) => i !== index)
      }
    }));
  };

  const handleResetAll = () => {
    if (!window.confirm('모든 요일의 시간표를 초기화하시겠습니까?')) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      weeklySchedule: {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: []
      }
    }));

    toast({
      title: '초기화 완료',
      description: '모든 일정이 삭제되었습니다.',
      status: 'info',
      duration: 2000
    });
  };

  const handleSaveSchedule = async () => {
    if (!editingSchedule && !formData.semesterId) {
      toast({
        title: '입력 오류',
        description: '학기를 선택해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    // 입력 가능 기간 체크 (신규 등록 및 수정 모두)
    const checkSemester = editingSchedule 
      ? semesters.find(s => s.id === editingSchedule.semesterId)
      : selectedSemester;

    if (checkSemester && !canRegisterSchedule(checkSemester)) {
      toast({
        title: '수정 불가',
        description: '시간표 입력 가능 기간이 아닙니다.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    try {
      setLoading(true);
      
      if (editingSchedule) {
        await updateAcademicSchedule(editingSchedule.id, {
          weeklySchedule: formData.weeklySchedule,
          isActive: formData.isActive
        });
        toast({
          title: '수정 완료',
          description: '시간표가 수정되었습니다.',
          status: 'success',
          duration: 3000
        });
      } else {
        // 학기 날짜 정보 포함하여 저장
        const scheduleData = {
          ...formData,
          startDate: selectedSemester.startDate,
          endDate: selectedSemester.endDate
        };
        await createAcademicSchedule(currentUser.uid, scheduleData);
        toast({
          title: '등록 완료',
          description: '시간표가 등록되었습니다.',
          status: 'success',
          duration: 3000
        });
      }
      
      await loadData();
      onClose();
    } catch (error) {
      console.error('저장 오류:', error);
      toast({
        title: '저장 실패',
        description: error.message || '시간표 저장에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      setLoading(true);
      await deleteAcademicSchedule(scheduleId);
      await loadData();
      
      toast({
        title: '삭제 완료',
        description: '시간표가 삭제되었습니다.',
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('삭제 오류:', error);
      toast({
        title: '삭제 실패',
        description: '시간표 삭제에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (schedule) => {
    try {
      setLoading(true);
      await updateAcademicSchedule(schedule.id, { isActive: !schedule.isActive });
      await loadData();
      
      toast({
        title: '변경 완료',
        description: `학기가 ${!schedule.isActive ? '활성화' : '비활성화'}되었습니다.`,
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('변경 오류:', error);
      toast({
        title: '변경 실패',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={textColor} mb={1}>
            시간표 관리
          </Heading>
          <Text fontSize="sm" color="gray.500">
            시간표를 등록하여 근무시간 충돌을 자동으로 체크하세요
          </Text>
        </Box>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon />}
          onClick={handleNewSchedule}
          isLoading={loading}
        >
          새 학기 추가
        </Button>
      </Flex>

      {/* 메인 컨텐츠 */}
      <Card overflowX={{ sm: 'scroll', xl: 'hidden' }}>
        <CardBody>
          {activeSchedule && (
            <Box mb={6} p={4} borderWidth="1px" borderRadius="lg" borderColor="blue.200" bg="blue.50">
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="lg" fontWeight="bold" color="blue.600">
                  현재 활성 학기: {activeSchedule.semesterName}
                </Text>
                <Badge colorScheme="green">활성</Badge>
              </Flex>
              <Text fontSize="sm" color="gray.600">
                {activeSchedule.startDate} ~ {activeSchedule.endDate}
              </Text>
            </Box>
          )}

          {schedules.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text color="gray.500">등록된 시간표가 없습니다.</Text>
              <Text color="gray.400" fontSize="sm" mt={2}>
                시간표를 등록하면 근무스케줄 신청 시 충돌을 자동으로 체크합니다.
              </Text>
            </Box>
          ) : (
            <Stack spacing={4}>
              {schedules.map((schedule) => (
                <Box
                  key={schedule.id}
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={borderColor}
                >
                  <Flex justify="space-between" align="center" mb={3}>
                    <Box>
                      <Flex align="center" gap={2}>
                        <Text fontSize="lg" fontWeight="bold">
                          {schedule.semesterName}
                        </Text>
                        {schedule.isActive && <Badge colorScheme="green">활성</Badge>}
                      </Flex>
                      <Text fontSize="sm" color="gray.500">
                        {schedule.startDate} ~ {schedule.endDate}
                      </Text>
                    </Box>
                    <Flex gap={2}>
                      <Switch
                        isChecked={schedule.isActive}
                        onChange={() => handleToggleActive(schedule)}
                        colorScheme="green"
                      />
                      <IconButton
                        icon={<EditIcon />}
                        size="sm"
                        onClick={() => handleEditSchedule(schedule)}
                        aria-label="수정"
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        aria-label="삭제"
                        isDisabled={schedule.isActive}
                      />
                    </Flex>
                  </Flex>

                  <Divider my={3} />

                  <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={3}>
                    {DAYS.map((day) => {
                      const classes = schedule.weeklySchedule[day] || [];
                      if (classes.length === 0) return null;
                      
                      return (
                        <Box key={day} p={2} bg="gray.50" borderRadius="md">
                          <Text fontWeight="bold" fontSize="sm" mb={1}>
                            {getDayNameKorean(day)}
                          </Text>
                          {classes.map((cls, idx) => (
                            <Text key={idx} fontSize="xs" color="gray.600">
                              {cls.start}-{cls.end} {cls.subject}
                            </Text>
                          ))}
                        </Box>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Stack>
          )}
        </CardBody>
      </Card>

      {/* 학기 추가/수정 모달 */}
      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        size={{ base: 'full', md: '6xl' }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }}>
          <ModalHeader>
            {editingSchedule ? '학업시간표 수정' : '새 학업시간표 추가'}
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody>
            <Stack spacing={4}>
              {!editingSchedule && (
                <>
                  <FormControl isRequired>
                    <FormLabel>학기 선택</FormLabel>
                    <Select
                      value={formData.semesterId}
                      onChange={(e) => handleSemesterSelect(e.target.value)}
                      placeholder="학기를 선택하세요"
                    >
                      {semesters
                        .filter(sem => {
                          // 입력 가능 기간인지 확인
                          if (!canRegisterSchedule(sem)) return false;
                          // 이미 시간표가 있는 학기 제외
                          const existingSemesterIds = schedules.map(s => s.semesterId);
                          return !existingSemesterIds.includes(sem.id);
                        })
                        .map(sem => (
                          <option key={sem.id} value={sem.id}>
                            {sem.semesterName} ({sem.startDate} ~ {sem.endDate})
                          </option>
                        ))}
                    </Select>
                  </FormControl>

                  {selectedSemester && (
                    <Box p={3} bg="blue.50" borderRadius="md">
                      <Text fontSize="sm" fontWeight="bold" color="blue.700">
                        선택한 학기: {selectedSemester.semesterName}
                      </Text>
                      <Text fontSize="xs" color="blue.600">
                        학기: {selectedSemester.startDate} ~ {selectedSemester.endDate}
                      </Text>
                      <Text fontSize="xs" color="blue.600">
                        입력 가능: {selectedSemester.registrationStartDate} ~ {selectedSemester.registrationEndDate}
                      </Text>
                    </Box>
                  )}
                </>
              )}

              {editingSchedule && (
                <Box p={3} bg="gray.100" borderRadius="md">
                  <Text fontSize="sm" fontWeight="bold">
                    학기: {formData.semesterName}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    학기 정보는 수정할 수 없습니다. 시간표만 수정 가능합니다.
                  </Text>
                </Box>
              )}

              <Divider />

              {/* 수업 추가 패널 (탭 위로 이동) */}
              <Box p={{ base: 3, md: 4 }} bg="blue.50" borderRadius="lg">
                <Heading size="sm" mb={4}>일정 추가</Heading>
                <VStack spacing={4} align="stretch">
                  <Grid 
                    templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} 
                    gap={3}
                  >
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="bold">시작시간</FormLabel>
                      <Input
                        type="time"
                        value={newClass.start}
                        onChange={(e) => setNewClass({ ...newClass, start: e.target.value })}
                        bg="white"
                        size="lg"
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="bold">종료시간</FormLabel>
                      <Input
                        type="time"
                        value={newClass.end}
                        onChange={(e) => setNewClass({ ...newClass, end: e.target.value })}
                        bg="white"
                        size="lg"
                      />
                    </FormControl>
                  </Grid>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="bold">일정명 (과목명)</FormLabel>
                    <Input
                      value={newClass.subject}
                      onChange={(e) => setNewClass({ ...newClass, subject: e.target.value })}
                      placeholder="예: 프로그래밍, 영어회화"
                      bg="white"
                      size="lg"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="bold">반복 요일 선택</FormLabel>
                    <CheckboxGroup
                      value={newClass.selectedDays}
                      onChange={(values) => setNewClass({ ...newClass, selectedDays: values })}
                    >
                      <Wrap spacing={{ base: 2, md: 3 }}>
                        {DAYS.map((day) => (
                          <WrapItem key={day}>
                            <Checkbox 
                              value={day} 
                              size="lg"
                              colorScheme="blue"
                              bg="white"
                              px={{ base: 2, md: 3 }}
                              py={2}
                              borderRadius="md"
                            >
                              <Text fontWeight="medium" fontSize={{ base: 'sm', md: 'md' }}>
                                {getDayNameKorean(day)}
                              </Text>
                            </Checkbox>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </CheckboxGroup>
                    <Text fontSize="xs" color="blue.700" mt={2}>
                      💡 같은 시간에 여러 요일에 있는 수업은 요일을 여러 개 선택하세요
                    </Text>
                  </FormControl>

                  <Button
                    colorScheme="blue"
                    size="lg"
                    leftIcon={<AddIcon />}
                    onClick={handleAddClass}
                    w="full"
                  >
                    선택한 요일에 일정 추가
                  </Button>
                </VStack>
              </Box>

              <Divider />

              <Heading size="md">등록된 주간 시간표</Heading>

              <Tabs variant="enclosed">
                <TabList overflowX="auto" overflowY="hidden">
                  {DAYS.map((day) => {
                    const count = formData.weeklySchedule[day]?.length || 0;
                    return (
                      <Tab key={day} whiteSpace="nowrap">
                        {getDayNameKorean(day)}
                        {count > 0 && (
                          <Badge ml={2} colorScheme="blue" fontSize="xs">
                            {count}
                          </Badge>
                        )}
                      </Tab>
                    );
                  })}
                </TabList>

                <TabPanels>
                  {DAYS.map((day) => (
                    <TabPanel key={day} px={{ base: 0, md: 4 }}>
                      <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                          <Thead>
                            <Tr>
                              <Th>시작</Th>
                              <Th>종료</Th>
                              <Th>일정명</Th>
                              <Th>삭제</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {(formData.weeklySchedule[day] || []).map((cls, idx) => (
                              <Tr key={idx}>
                                <Td fontSize="sm">{cls.start}</Td>
                                <Td fontSize="sm">{cls.end}</Td>
                                <Td fontWeight="medium">{cls.subject}</Td>
                                <Td>
                                  <IconButton
                                    icon={<DeleteIcon />}
                                    size="xs"
                                    colorScheme="red"
                                    onClick={() => handleDeleteClass(day, idx)}
                                    aria-label="삭제"
                                  />
                                </Td>
                              </Tr>
                            ))}
                            {(!formData.weeklySchedule[day] || formData.weeklySchedule[day].length === 0) && (
                              <Tr>
                                <Td colSpan={4} textAlign="center" color="gray.400" py={6}>
                                  등록된 일정이 없습니다
                                </Td>
                              </Tr>
                            )}
                          </Tbody>
                        </Table>
                      </Box>
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </Stack>
          </ModalBody>

          <ModalFooter justifyContent="space-between">
            <Button
              colorScheme="red"
              variant="outline"
              onClick={handleResetAll}
              size="sm"
            >
              전체 초기화
            </Button>
            <Flex gap={3}>
              <Button variant="ghost" onClick={onClose}>
                취소
              </Button>
              <Button colorScheme="blue" onClick={handleSaveSchedule} isLoading={loading}>
                {editingSchedule ? '수정' : '저장'}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default AcademicSchedule;
